import {
  Box,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  Drawer,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import IosShareIcon from "@mui/icons-material/IosShare";
import CheckIcon from "@mui/icons-material/Check";
import RestaurantItemImage from "../restaurant/RestaurantItemImage";
import DealTags from "./DealTags";
import DealInfoSidebar from "./DealInfoSidebar";
import CustomButton from "../ui/CustomButton";
import { saveDeal, unsaveDeal } from "../../utils/userPreferences";
import { useRequireAuth } from "../../hooks/useRequireAuth";
import { useDeal } from "../../hooks/useDeal";
import { useAuth } from "../../contexts/AuthContext";
import { toSlug } from "../../utils/urlSlug";
import DealDetailsSkeleton from "../skeletons/DealDetailsSkeleton";
import logger from "../../utils/logger";
import { trackEvent } from "../../utils/analytics";
import { closeIconButtonSx } from "../../constants/closeIconButtonSx";
import { appBackgroundColor } from "../../theme";
import { uiNeutrals } from "../../constants/uiNeutrals";
import ReminderContextNudge from "../reminders/ReminderContextNudge";
import { useReminderSetupStatus } from "../reminders/hooks/useReminderSetupStatus";
import { useNavigateToReminders } from "../reminders/useNavigateToReminders";
import {
  markReminderContextNudgeDismissed,
  shouldShowReminderContextNudge,
} from "../reminders/reminderPromptStorage";

const REPORT_WHATSAPP_NUMBER = "60189426834";

const isDealCompleteForViewItemAnalytics = (d) => {
  if (!d) return false;
  const title = typeof d.title === "string" ? d.title.trim() : "";
  const restaurant =
    typeof d.brand?.name === "string" ? d.brand.name.trim() : "";
  return Boolean(title && restaurant && (d.shortId || d._id));
};

const DealDetails = ({ deal: dealProp, onClose, isOpen }) => {
  const { dealId } = useParams();
  const isRouteMode = Boolean(dealId && !dealProp);
  const navigate = useNavigate();
  const requireAuth = useRequireAuth();
  const { isAuthenticated } = useAuth();
  const [deal, setDeal] = useState(dealProp);
  const [fullDealData, setFullDealData] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [restaurant, setRestaurant] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [loadingSavedFollowed, setLoadingSavedFollowed] = useState(true);
  const [shareToastOpen, setShareToastOpen] = useState(false);
  const [saveToastOpen, setSaveToastOpen] = useState(false);
  const [detailNudgeVisible, setDetailNudgeVisible] = useState(true);
  const navigateToReminders = useNavigateToReminders();
  const { isReminderSetup } = useReminderSetupStatus(Boolean(isOpen));
  const [dealIdToastOpen, setDealIdToastOpen] = useState(false);
  /** Keeps drawer in DOM through close transition (parent clears `deal` + `isOpen` together). */
  const [drawerSessionActive, setDrawerSessionActive] = useState(false);

  // Load deal from URL if used as route component
  const shouldLoadFromUrl = dealId && !dealProp;
  const {
    deal: dealFromUrl,
    loading: loadingFromUrl,
    error: errorFromUrl,
  } = useDeal(shouldLoadFromUrl ? dealId : null, {
    enabled: shouldLoadFromUrl,
  });

  // Update deal state when loaded from URL
  // NOTE: onSuccess was removed in TanStack Query v5 – use useEffect instead.
  useEffect(() => {
    if (dealFromUrl && shouldLoadFromUrl) {
      setDeal(dealFromUrl);
      setFullDealData(dealFromUrl);
    }
  }, [dealFromUrl, shouldLoadFromUrl]);

  useEffect(() => {
    if (isOpen) setDetailNudgeVisible(true);
  }, [isOpen, deal?._id, deal?.shortId]);

  // Lazy load full deal details when drawer is opened
  // Only load if we have a deal with shortId, drawer is open, and we're not loading from URL
  const shouldLoadFullDetails = !shouldLoadFromUrl && deal?.shortId && isOpen;
  // NOTE: onSuccess is ignored in TanStack Query v5 – read data directly from the hook return value.
  const { deal: fullDealFromHook, loading: loadingFullDeal } = useDeal(
    shouldLoadFullDetails ? deal?.shortId : null,
    { enabled: shouldLoadFullDetails },
  );

  // Sync fullDealData whenever the hook resolves (replaces the dead onSuccess callback)
  useEffect(() => {
    if (fullDealFromHook) {
      setFullDealData(fullDealFromHook);
    }
  }, [fullDealFromHook]);

  const dealApiLoading = loadingFromUrl || loadingFullDeal;
  const loading = dealApiLoading || loadingSavedFollowed;

  const displayDeal = fullDealData || deal || dealFromUrl;

  // Sync deal state with dealProp
  useEffect(() => {
    if (dealProp) {
      setDeal(dealProp);
    }
  }, [dealProp]);

  useEffect(() => {
    if (!isRouteMode && isOpen && dealProp) {
      setDrawerSessionActive(true);
    }
  }, [isRouteMode, isOpen, dealProp]);

  // Check saved state (only when deal or auth changes, not on every render)
  useEffect(() => {
    if (!deal) {
      setLoadingSavedFollowed(false);
      return;
    }

    setLoadingSavedFollowed(true);

    if (isAuthenticated) {
      const checkSavedState = async () => {
        try {
          const { getSavedDeals, isDealSaved } = await import(
            "../../utils/userPreferences"
          );
          await getSavedDeals();
          setIsSaved(deal.shortId ? isDealSaved(deal.shortId) : false);
        } finally {
          setLoadingSavedFollowed(false);
        }
      };
      checkSavedState();
    } else {
      setIsSaved(false);
      setLoadingSavedFollowed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal?.shortId, isAuthenticated]);

  const viewItemTrackedRef = useRef(null);
  useEffect(() => {
    if (shouldLoadFromUrl && errorFromUrl) {
      return;
    }
    const currentDeal = fullDealData || deal || dealFromUrl;
    const dealId = currentDeal?.shortId || currentDeal?._id;
    if (dealApiLoading || !dealId || !currentDeal) {
      return;
    }
    if (!isDealCompleteForViewItemAnalytics(currentDeal)) {
      return;
    }
    if (!currentDeal.brand || typeof currentDeal.brand?.name !== "string") {
      return;
    }
    if (viewItemTrackedRef.current === dealId) {
      return;
    }
    viewItemTrackedRef.current = dealId;
    const item_name =
      typeof currentDeal.title === "string" ? currentDeal.title.trim() : "";
    const restaurant_name = currentDeal.brand.name.trim();
    if (!item_name || !restaurant_name) {
      return;
    }
    trackEvent("view_item", {
      item_id: String(dealId),
      item_name,
      restaurant_name,
      category: currentDeal.time_of_day || currentDeal.brand?.category || "",
    });
  }, [
    deal,
    dealApiLoading,
    dealFromUrl,
    errorFromUrl,
    fullDealData,
    shouldLoadFromUrl,
  ]);

  // Set restaurant data (separate effect to avoid unnecessary re-runs)
  useEffect(() => {
    const currentDeal = fullDealData || deal || dealFromUrl;
    if (currentDeal?.brand?._id) {
      setRestaurant({
        _id: currentDeal.brand._id,
        name: currentDeal.brand.name,
        area: Array.isArray(currentDeal.area)
          ? currentDeal.area[0]
          : currentDeal.area,
        address: currentDeal.brand.address || "",
        category: currentDeal.brand.category || "Restaurant",
        contact: {
          website: currentDeal.brand.website || "",
          phone: currentDeal.brand.phone || "",
          whatsapp: currentDeal.brand.phone || "",
        },
        allowDelivery: false,
        allowPickup: true,
        type: "featured",
      });
    } else {
      setRestaurant(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal?.brand?._id, fullDealData?.brand?._id, dealFromUrl?.brand?._id]);

  const handleSaveToggle = async () => {
    // Only use shortId for saved deals - deals should always have shortId
    if (!deal?.shortId) {
      logger.warn("Cannot save deal: missing shortId");
      return;
    }

    // If unsaving, allow it without auth check
    if (isSaved) {
      setIsSaved(false); // Optimistic update
      const success = await unsaveDeal(deal.shortId);

      if (!success) {
        setIsSaved(true); // Revert on failure
      } else {
        window.dispatchEvent(
          new CustomEvent("savedDealsChanged", {
            detail: { unsavedShortId: deal.shortId },
          }),
        );
      }
      return;
    }

    // If saving, require authentication
    requireAuth(async () => {
      setIsSaved(true); // Optimistic update
      setSaveToastOpen(true);
      const success = await saveDeal(deal.shortId);
      if (!success) {
        setIsSaved(false); // Revert on failure
        setSaveToastOpen(false);
      } else {
        window.dispatchEvent(new Event("savedDealsChanged"));
        window.dispatchEvent(new Event("loopsDealSaved"));
      }
    });
  };

  const handleCopyAddress = async () => {
    try {
      await navigator.clipboard.writeText(restaurant?.address || "");
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch (err) {
      logger.error("Failed to copy: ", err);
    }
  };

  const handleShare = async () => {
    try {
      const currentDealId = displayDeal?.shortId || displayDeal?._id || dealId;
      if (!currentDealId) {
        logger.error("Cannot share: missing deal ID");
        return;
      }

      // Construct the full URL
      const dealUrl = `${window.location.origin}/deal/${currentDealId}`;

      const shareTitle =
        typeof displayDeal?.title === "string" && displayDeal.title.trim()
          ? displayDeal.title.trim()
          : "Deal";
      const restaurantName =
        typeof displayDeal?.brand?.name === "string" && displayDeal.brand.name
          ? displayDeal.brand.name
          : "";
      const shareText = restaurantName ? `${restaurantName} • ${shareTitle}` : shareTitle;

      if (navigator.share) {
        try {
          await navigator.share({
            title: shareTitle,
            text: shareText,
            url: dealUrl,
          });
          return;
        } catch (err) {
          // User canceled the share sheet
          if (err?.name === "AbortError") return;
          logger.warn("Native share failed, falling back to copy link:", err);
        }
      }

      // Fallback: copy to clipboard
      await navigator.clipboard.writeText(dealUrl);
      setShareToastOpen(true);
    } catch (err) {
      logger.error("Failed to copy deal link: ", err);
    }
  };

  const handleCopyDealId = async () => {
    const id = displayDeal?.shortId || displayDeal?._id;
    if (!id) return;
    try {
      await navigator.clipboard.writeText(id);
      setDealIdToastOpen(true);
    } catch (err) {
      logger.error("Failed to copy deal ID:", err);
    }
  };

  const handleClose = isRouteMode
    ? () => {
        const returnUrl = sessionStorage.getItem("deals_previous_url");
        if (returnUrl) {
          sessionStorage.removeItem("deals_previous_url");
          navigate(returnUrl);
        } else {
          navigate("/deals");
        }
      }
    : () => {
        if (onClose) onClose();
      };

  // Handle route mode error states
  if (isRouteMode) {
    if (loading) {
      return <DealDetailsSkeleton onClose={handleClose} />;
    }
    if (errorFromUrl) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            p: 4,
            minHeight: "50vh",
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            Deal not found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {errorFromUrl || "The deal you're looking for doesn't exist."}
          </Typography>
          <CustomButton
            variant="primary"
            onClick={() => {
              const returnUrl = sessionStorage.getItem("deals_previous_url");
              if (returnUrl) {
                sessionStorage.removeItem("deals_previous_url");
                navigate(returnUrl);
              } else {
                navigate("/deals");
              }
            }}
          >
            Back to Explore
          </CustomButton>
        </Box>
      );
    }
    if (!dealFromUrl && !deal && !loadingFromUrl) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            p: 4,
            minHeight: "50vh",
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            Deal not found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The deal you're looking for doesn't exist.
          </Typography>
          <CustomButton
            variant="primary"
            onClick={() => {
              const returnUrl = sessionStorage.getItem("deals_previous_url");
              if (returnUrl) {
                sessionStorage.removeItem("deals_previous_url");
                navigate(returnUrl);
              } else {
                navigate("/deals");
              }
            }}
          >
            Back to Explore
          </CustomButton>
        </Box>
      );
    }
    if (!dealFromUrl && !deal) {
      return <DealDetailsSkeleton onClose={handleClose} />;
    }

    // Graceful expired deal UI — preserves the URL/SEO authority instead of 404
    const expiredDeal = dealFromUrl || deal;
    if (expiredDeal?._expired || expiredDeal?.isActive === false) {
      const restaurantName = expiredDeal._restaurantName || expiredDeal.brand?.name || "";
      const restaurantSlug = expiredDeal._restaurantSlug || (restaurantName ? restaurantName.trim().replace(/'/g, "").replace(/[^a-zA-Z0-9\s-]/g, "").replace(/\s+/g, "-") : "");
      const expiredTitle = expiredDeal.title
        ? `${expiredDeal.title}${restaurantName ? ` at ${restaurantName}` : ""} — Loops`
        : "Deal — Loops";
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            p: 4,
            minHeight: "60vh",
            textAlign: "center",
          }}
        >
          <Helmet>
            <title>{expiredTitle}</title>
            <meta name="robots" content="noindex, follow" />
            {restaurantSlug && (
              <link rel="canonical" href={`https://loopsfood.com/${restaurantSlug}`} />
            )}
          </Helmet>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            This deal has expired
          </Typography>
          {restaurantName && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 320 }}>
              {`${expiredDeal.title ? `"${expiredDeal.title}" is` : "This deal is"} no longer available, but ${restaurantName} may have other active deals for you.`}
            </Typography>
          )}
          {restaurantSlug ? (
            <CustomButton
              variant="primary"
              onClick={() => navigate(`/${restaurantSlug}`)}
            >
              {`See all deals at ${restaurantName || "this restaurant"}`}
            </CustomButton>
          ) : (
            <CustomButton variant="primary" onClick={() => navigate("/deals")}>
              Browse all deals
            </CustomButton>
          )}
        </Box>
      );
    }
  }

  // Drawer mode: do not unmount on `isOpen` alone — wait for Slide exit (onExited).
  if (!isRouteMode && !drawerSessionActive && !isOpen) return null;

  const seoTitle = displayDeal
    ? `${displayDeal.title}${displayDeal.brand?.name ? ` at ${displayDeal.brand.name}` : ""} — Loops`
    : "Deal — Loops";
  const seoDescription = displayDeal?.brand?.name
    ? `Check out ${displayDeal.title} at ${displayDeal.brand.name} on Loops. Find food deals near you in Malaysia.`
    : "Find food deals near you in Malaysia on Loops.";
  const seoImage = displayDeal?.imageUrl || "https://loopsfood.com/assets/loops-og-image.png";
  const seoUrl = `https://loopsfood.com/deal/${displayDeal?.shortId || displayDeal?._id || dealId}`;

  const dealJsonLd = displayDeal ? JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Offer",
    name: displayDeal.title,
    url: seoUrl,
    ...(displayDeal.description && { description: displayDeal.description }),
    ...(displayDeal.imageUrl && { image: displayDeal.imageUrl }),
    priceCurrency: "MYR",
    ...(displayDeal.price != null && { price: displayDeal.price }),
    availability: "https://schema.org/InStock",
    ...(displayDeal.brand?.name && {
      offeredBy: {
        "@type": "Restaurant",
        name: displayDeal.brand.name,
        ...(displayDeal.brand.website && { url: displayDeal.brand.website }),
      },
    }),
    seller: { "@type": "Organization", name: "Loops", url: "https://loopsfood.com" },
  }) : null;

  // Show skeleton while loading, or when deal data is not yet available (drawer edge cases)
  const content = loading ? (
    <DealDetailsSkeleton onClose={handleClose} />
  ) : !displayDeal ? (
    <DealDetailsSkeleton onClose={handleClose} />
  ) : (
    <Box
      sx={{
        bgcolor: "transparent",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        width: "100%",
        height: "100%",
        minHeight: 0,
      }}
    >
      {isRouteMode && (
        <Helmet>
          <title>{seoTitle}</title>
          <meta name="description" content={seoDescription} />
          <link rel="canonical" href={seoUrl} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={seoUrl} />
          <meta property="og:title" content={seoTitle} />
          <meta property="og:description" content={seoDescription} />
          <meta property="og:image" content={seoImage} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={seoTitle} />
          <meta name="twitter:description" content={seoDescription} />
          <meta name="twitter:image" content={seoImage} />
          {dealJsonLd && (
            <script type="application/ld+json">{dealJsonLd}</script>
          )}
        </Helmet>
      )}
      {(() => {
        const safeTop =
          "calc(constant(safe-area-inset-top) + env(safe-area-inset-top))";
        const safeBottom =
          "calc(constant(safe-area-inset-bottom) + env(safe-area-inset-bottom))";

        const headerBarPx = 56;
        const footerBarPx = 52;

        return (
          <>
            {isRouteMode && (
              <Helmet>
                <title>{seoTitle}</title>
                <meta name="description" content={seoDescription} />
                <meta property="og:title" content={seoTitle} />
                <meta property="og:description" content={seoDescription} />
                <meta property="og:image" content={seoImage} />
                <meta property="og:url" content={seoUrl} />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={seoTitle} />
                <meta name="twitter:description" content={seoDescription} />
                <meta name="twitter:image" content={seoImage} />
                <link rel="canonical" href={seoUrl} />
                {dealJsonLd && (
                  <script type="application/ld+json">{dealJsonLd}</script>
                )}
              </Helmet>
            )}
            {/* Header — FilterPage-style row; close on the right */}
            <Box
              sx={{
                flexShrink: 0,
                boxSizing: "border-box",
                bgcolor: "rgba(253, 252, 251, 0.78)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                pt: safeTop,
                borderBottom: `1px solid ${uiNeutrals.rowDivider}`,
              }}
            >
              <Box
                sx={{
                  height: `${headerBarPx}px`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  px: 2,
                  minWidth: 0,
                }}
              >
                <Box
                  onClick={() => {
                    if (displayDeal?.brand?.name) {
                      const slug = toSlug(displayDeal.brand?.name ?? "");
                      navigate(`/deals/yours/restaurant/${slug}`);
                      if (!isRouteMode && onClose) onClose();
                    }
                  }}
                  sx={{
                    cursor: displayDeal?.brand?.name ? "pointer" : "default",
                    minWidth: 0,
                    flex: 1,
                    "&:hover": displayDeal?.brand?.name
                      ? { opacity: 0.75 }
                      : undefined,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "16px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {displayDeal?.brand?.name || "Brand"}
                  </Typography>
                </Box>

                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                  }}
                  edge="end"
                  aria-label="Close"
                  sx={closeIconButtonSx}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Scrollable content */}
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                overscrollBehavior: "contain",
                WebkitOverflowScrolling: "touch",
                touchAction: "pan-y",
                bgcolor: appBackgroundColor,
                "&::-webkit-scrollbar": { display: "none" },
                msOverflowStyle: "none",
                scrollbarWidth: "none",
              }}
            >
              {/* Image */}
              <Box
                sx={{
                  width: "100%",
                  maxWidth: "260px",
                  height: "auto",
                  aspectRatio: "1/1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  bgcolor: "background.default",
                  overflow: "hidden",
                  mt: 2,
                  flexShrink: 0,
                  mx: "auto",
                }}
              >
                <RestaurantItemImage
                  restaurantName={displayDeal?.brand?.name || "Brand"}
                  imageUrl={displayDeal?.imageUrl}
                  useDefaultFallback={false}
                  size="100%"
                  className="restaurant-img"
                  sx={{
                    width: "100%",
                    height: "100%",
                    transition: "transform 0.3s ease",
                  }}
                />
              </Box>

              <Box sx={{ position: "relative" }}>
                {/* Deal Summary badge */}
                {displayDeal?.dealSummary && (
                  <Box
                    sx={{
                      position: "absolute",
                      backgroundColor: "white",
                      color: "black",
                      borderRadius: "25px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      mx: 2,
                      mt: 2,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: "500",
                        fontSize: "14px",
                      }}
                    >
                      {displayDeal?.dealSummary}
                    </Typography>
                  </Box>
                )}

                {/* Title + deal id (tap id to copy) */}
                <Box sx={{ px: 2.5, pt: 1.5, mb: 1.5 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      mb: 0.5,
                      fontSize: "24px",
                      lineHeight: 1.25,
                    }}
                  >
                    {displayDeal?.title}
                  </Typography>
                </Box>

                {/* Tags */}
                <Box sx={{ px: 2.5, mb: 1 }}>
                  <DealTags
                    fulfillment={displayDeal?.fulfillment}
                    timeOfDay={displayDeal?.time_of_day}
                  />
                </Box>

                {/* Description */}
                {displayDeal?.description &&
                  displayDeal.description.trim() !== "" && (
                    <Typography
                      sx={{
                        px: 2.5,
                        my: 2,
                        color: "#666",
                        lineHeight: 1.6,
                        whiteSpace: "pre-line",
                        fontSize: "15px",
                      }}
                    >
                      {displayDeal.description}
                    </Typography>
                  )}

                {isOpen &&
                !isReminderSetup &&
                detailNudgeVisible &&
                shouldShowReminderContextNudge() ? (
                  <Box sx={{ px: 2.5, mb: 2 }}>
                    <ReminderContextNudge
                      compact
                      title="Want updates like this?"
                      description="Get Telegram alerts for similar and expiring deals."
                      onSetUp={() => navigateToReminders("deal_detail")}
                      onDismiss={() => {
                        markReminderContextNudgeDismissed();
                        setDetailNudgeVisible(false);
                      }}
                    />
                  </Box>
                ) : null}

                {/* Deal Info Sidebar */}
                {restaurant && (
                  <DealInfoSidebar
                    restaurant={restaurant}
                    deal={displayDeal}
                    copyFeedback={copyFeedback}
                    handleCopyAddress={handleCopyAddress}
                    loading={loading}
                    reportWhatsAppNumber={REPORT_WHATSAPP_NUMBER}
                  />
                )}
              </Box>
            </Box>

            {/* Footer — active state + actions (FilterPage-style bar) */}
            <Box
              sx={{
                flexShrink: 0,
                boxSizing: "border-box",
                bgcolor: "rgba(253, 252, 251, 0.80)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                pb: safeBottom,
                borderTop: `1px solid ${uiNeutrals.rowDivider}`,
              }}
            >
              <Box
                sx={{
                  height: `${footerBarPx}px`,
                  display: "flex",
                  alignItems: "center",
                    justifyContent: "space-between",
                  gap: 0.5,
                  px: 2,
                  pt: 0.5,
                }}
              >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "9999px",
                        bgcolor:
                          displayDeal?.isActive !== false ? "#dcfce7" : "#fee2e2",
                      }}
                    >
                      <Typography
                        sx={{
                          color:
                            displayDeal?.isActive !== false
                              ? "#16a34a"
                              : "#dc2626",
                          fontSize: "12px",
                          fontWeight: 600,
                          textTransform: "capitalize",
                        }}
                      >
                        {displayDeal?.isActive !== false ? "Active" : "Inactive"}
                      </Typography>
                    </Box>

                    <Box
                      component="button"
                      type="button"
                      aria-label="Copy deal ID"
                      onClick={handleCopyDealId}
                      disabled={!(displayDeal?.shortId || displayDeal?._id)}
                      sx={{
                        border: "none",
                        background: "transparent",
                        padding: 0,
                        cursor:
                          displayDeal?.shortId || displayDeal?._id
                            ? "pointer"
                            : "default",
                        fontFamily: "inherit",
                      }}
                    >
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: "9999px",
                          bgcolor: "#f3f4f6",
                          border: "1px solid #e5e7eb",
                          "&:hover":
                            displayDeal?.shortId || displayDeal?._id
                              ? { borderColor: "#d1d5db" }
                              : undefined,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#111827",
                            letterSpacing: "0.02em",
                          }}
                        >
                          {displayDeal?.shortId || displayDeal?._id || "—"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <IconButton
                    onClick={handleShare}
                    disableRipple
                    disableTouchRipple
                    sx={{ p: 1, "&:active": { bgcolor: "transparent" } }}
                  >
                    <IosShareIcon sx={{ color: "#000" }} />
                  </IconButton>
                  <Box
                    onClick={handleSaveToggle}
                    sx={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 1,
                      borderRadius: "8px",
                      WebkitTapHighlightColor: "transparent",
                      "&:active": { bgcolor: "transparent" },
                    }}
                  >
                    {isSaved ? (
                      <BookmarkIcon sx={{ fontSize: 28, color: "#000" }} />
                    ) : (
                      <BookmarkBorderIcon
                        sx={{ fontSize: 28, color: "#000" }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </>
        );
      })()}

      {/* Share Success Toast */}
      <Snackbar
        open={shareToastOpen}
        autoHideDuration={3000}
        onClose={() => setShareToastOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ zIndex: 20020 }}
      >
        <Alert
          onClose={() => setShareToastOpen(false)}
          severity="success"
          icon={<CheckIcon />}
          sx={{ width: "100%" }}
        >
          Deal link copied to clipboard. Paste it anywhere to share it!
        </Alert>
      </Snackbar>

      {/* Save Toast */}
      <Snackbar
        open={saveToastOpen}
        autoHideDuration={2000}
        onClose={() => setSaveToastOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ zIndex: 20020 }}
      >
        <Alert
          onClose={() => setSaveToastOpen(false)}
          severity="success"
          icon={<BookmarkIcon sx={{ fontSize: 18 }} />}
          sx={{ width: "100%", alignItems: "center" }}
        >
          Deal saved!
        </Alert>
      </Snackbar>

      {/* Deal ID copied Toast */}
      <Snackbar
        open={dealIdToastOpen}
        autoHideDuration={2000}
        onClose={() => setDealIdToastOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ zIndex: 20020 }}
      >
        <Alert
          onClose={() => setDealIdToastOpen(false)}
          severity="success"
          icon={<CheckIcon />}
          sx={{ width: "100%" }}
        >
          Deal ID copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );

  if (isRouteMode) {
    const metaTitle = displayDeal?.title
      ? `${displayDeal.title}${displayDeal.brand?.name ? ` at ${displayDeal.brand.name}` : ""} — Loops`
      : "Food Deal — Loops";
    const metaDescription = displayDeal?.description
      ? displayDeal.description.slice(0, 160)
      : `Find great food deals in Malaysia on Loops.`;
    const metaImage = displayDeal?.imageUrl || "https://loopsfood.com/assets/loops-og-image.png";
    const metaUrl = `https://loopsfood.com/deal/${displayDeal?.shortId || displayDeal?._id || dealId}`;

    return (
      <Box
        sx={{
          height: "100vh",
          bgcolor: appBackgroundColor,
        }}
      >
        <Helmet>
          <title>{metaTitle}</title>
          <meta name="description" content={metaDescription} />
          <link rel="canonical" href={metaUrl} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={metaUrl} />
          <meta property="og:title" content={metaTitle} />
          <meta property="og:description" content={metaDescription} />
          <meta property="og:image" content={metaImage} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={metaTitle} />
          <meta name="twitter:description" content={metaDescription} />
          <meta name="twitter:image" content={metaImage} />
        </Helmet>
        {content}
      </Box>
    );
  }

  return (
    <Drawer
      open={Boolean(isOpen)}
      onClose={handleClose}
      anchor="bottom"
      ModalProps={{
        keepMounted: false,
      }}
      SlideProps={{
        appear: true,
        easing: {
          enter: "cubic-bezier(0, 0, 0.2, 1)",
          exit: "cubic-bezier(0.4, 0, 1, 1)",
        },
        onExited: () => {
          setDrawerSessionActive(false);
        },
      }}
      transitionDuration={{ enter: 280, exit: 220 }}
      sx={{ zIndex: 20000 }}
      PaperProps={{
        elevation: 0,
        sx: {
          height: "min(96dvh, 940px)",
          maxHeight: "min(96dvh, 940px)",
          width: "100%",
          maxWidth: "100%",
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          bgcolor: "transparent",
          borderTop: uiNeutrals.frostedToggleBorder,
          boxShadow: "0 -10px 36px rgba(0, 0, 0, 0.1)",
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(255, 255, 255, 0.2)",
          backdropFilter: "blur(5px) saturate(1.05)",
          WebkitBackdropFilter: "blur(5px) saturate(1.05)",
        },
      }}
    >
      {content}
    </Drawer>
  );
};

export default DealDetails;
